import Phaser from "phaser";
import type { TabViewContract } from "@yjsgames/phaser-ui/tab-registry";
import {
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
  SCROLLABLE_LIST_EVENT,
  ScrollableList,
} from "@yjsgames/phaser-ui";
import type { Rect } from "@yjsgames/phaser-ui/panel-content";
import { isInsideSlotsRow } from "./slotDropZone.js";
import { attachScrollCellDrag } from "./scrollListDragHandle.js";
import {
  FONT_FAMILY,
  hexToNum,
  PRIMARY_BUTTON_STYLE,
  RARITY_COLOR,
  setButtonEnabled,
  TYPO,
} from "./uiTokens.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import type { TextDirection } from "@yjsgames/i18n";
import { FUSION_MATERIAL_COUNT } from "../systems/ModuleFusion.js";
import {
  resolveFusionMaterialButtonState,
  type MaterialButtonState,
} from "./moduleCompositionUiState.js";
import { computeModuleFusionLayout } from "./moduleFusionLayout.js";
import { moduleRowsSignature } from "./moduleRowsSignature.js";
import { previewSymbolFor } from "../data/modulePorts.js";
import type { ModuleRarity } from "../modules/ProjectileModule.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { TipCarouselView } from "./TipCarouselView.js";
import { SortFilterControl } from "./SortFilterControl.js";
import { applySort, type SortState } from "./sortFilter.js";
import {
  DEFAULT_PALETTE_SORT,
  PALETTE_KEY_EXTRACTORS,
  PALETTE_SORT_KEYS,
  paletteTieBreak,
  type PaletteSortKey,
} from "./paletteSort.js";

/** 융합 탭 표시용 모듈 1개 — GameScene.buildModuleFusionData가 미배치·미융합 인스턴스를 전달(merge 행과 동형). */
export interface ModuleFusionRow {
  readonly instanceId: string;
  readonly definitionId: string;
  readonly name: string;
  readonly level: number;
  readonly rarity: ModuleRarity;
  readonly shapeId: string;
  /** 슬롯 점유 모양(single/vert2/horz2) — 융합 매칭 기준(같은 footprint 2개). */
  readonly footprintId: string;
}

/** 현재 융합 1회 비용 (ModuleFusionController.cost 결과를 뷰로 전달). 버튼 라벨/표시용. */
export type FusionCostView =
  | { readonly kind: "free" }
  | { readonly kind: "ad" }
  | { readonly kind: "diamond"; readonly amount: number };

export interface ModuleFusionTabData {
  /** 미배치·미융합 보유 모듈 (전투 그리드 배치분 + 융합 산출물 제외). */
  readonly modules: readonly ModuleFusionRow[];
  /** 현재 융합 1회 비용 — 첫 회 광고(adFree면 무료), 2회째부터 다이아 고정. */
  readonly cost: FusionCostView;
}

export interface ModuleFusionTabViewOptions {
  /** 융합 실행 — 재료 instanceId 2개. GameScene이 canFuse 재검증 + 비용 결제(광고/다이아) 후 inventory 갱신. */
  readonly onFuse: (materialIds: readonly string[]) => void;
  /** 하단 셀 탭 — 모듈 정보 팝업(팝업 하단 등록 버튼이 toggleMaterial 호출). */
  readonly onCellInfo: (instanceId: string) => void;
}

const TILE_W = 78;
const TILE_H = 52;
const TILE_GAP = 6;
/** 정렬 드롭다운 폭/높이 — sortRow 우측 인라인(합성 탭과 동형, 얇게). */
const SORT_W = 120;
const SORT_H = 12;
/** 융합/비우기 버튼 폭·간격 — 생성자/resize 공용. */
const FUSE_BTN_W = 130;
const CLEAR_BTN_W = 90;
const BTN_GAP = 12;
const POOL_OVERSCAN_ROWS = 2;

function setVisibleActive<T extends Phaser.GameObjects.GameObject & { setVisible: (visible: boolean) => T }>(
  obj: T,
  visible: boolean,
): void {
  obj.setVisible(visible);
  (obj as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown }).setActive?.(visible);
}

/** 셀/슬롯 라벨 — 파이프 심볼(footprint 방향) 윗줄 + 이름 Lv 아랫줄 (merge 탭뷰 동형). */
function tileLabelText(row: ModuleFusionRow): string {
  return `${previewSymbolFor(row.shapeId, row.footprintId)}\n${t("palette.tile", {
    name: row.name,
    level: row.level,
  })}`;
}


interface CellEntry {
  /** 현재 표시 중인 row. 잉여 풀 셀(rows 초과)은 undefined — 핸들러/선택 갱신이 동적 조회. */
  row?: ModuleFusionRow;
  bg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  /** 현재 컨테이너 로컬 home 좌표(i 기반). 드래그 스냅백 + relayout에 사용. */
  homeX: number;
  homeY: number;
}

/**
 * 모듈 융합 탭 — 상단 융합 슬롯 2칸 + 결과 미리보기 + 융합 버튼(비용 표기), 하단 미배치·미융합 모듈 그리드.
 * 합성 탭뷰(ModuleMergeTabView)를 그대로 따르되: ① 슬롯 2칸 ② 선택 매칭 기준 = **같은 footprint**(종류 무관)
 * ③ 융합 버튼 라벨에 비용(광고/다이아) 표기 ④ 융합 규칙 설명은 제목 아래 팁 캐러셀(TipCarouselView)로, 융합 산출물 제외 주의는 그 팁 항목으로 표시.
 * 재료 등록/해제는 클릭(셀 탭/정보팝업) + **드래그**(셀→슬롯행 드롭=등록, 슬롯→밖 드롭=해제) 둘 다 지원.
 * 실제 융합(비용 결제 + inventory 갱신)은 GameScene.performModuleFusion이 canFuse 재검증 후 수행(stale 방지).
 *
 * RTL: title/prompt 정렬 + 슬롯 순서 + 미배치 그리드 col 미러. prompt(재료 선택 1줄)는 `DirectionalText`(applyBidi)로
 * 정렬 + Bidi reorder(아랍어 등 RTL 스크립트 우→좌). 결과/버튼은 가로 중앙이라 불변.
 */
export class ModuleFusionTabView implements TabViewContract<ModuleFusionTabData> {
  /** 콘텐츠 최상단 팁 캐러셀 (재사용 컴포넌트, tab="moduleFusion"). 제목·프롬프트 행은 제거(하단 [합성]/[융합] 서브탭 라벨로 구분). */
  private readonly tipView: TipCarouselView;
  private readonly slots: { bg: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }[] = [];
  private readonly plusTexts: Phaser.GameObjects.Text[] = [];
  private readonly resultText: Phaser.GameObjects.Text;
  private readonly fuseBtn: NineSliceButton;
  private readonly clearBtn: NineSliceButton;
  private readonly scrollList: ScrollableList;
  private listWidth: number;
  private readonly cells: CellEntry[] = [];
  private rows: readonly ModuleFusionRow[] = [];
  private cost: FusionCostView = { kind: "ad" };
  /** 재사용 정렬 컨트롤(모양/파츠/등급) — 리스트 헤더(prompt 행) 우측. */
  private readonly sortControl: SortFilterControl<PaletteSortKey>;
  /** 현재 정렬 상태 — applyData/onSortChange가 this.rows에 적용. */
  private sortState: SortState<PaletteSortKey> = DEFAULT_PALETTE_SORT;
  /** 현재 선택된 재료 instanceId (최대 FUSION_MATERIAL_COUNT). 첫 원소가 기준(footprint). */
  private selectedIds: string[] = [];
  /** 현재 방향 — 그리드 col 미러·슬롯 순서·prompt 정렬에 사용. relayout(dir)이 갱신. */
  private dir: TextDirection = getTextDirection();
  /** 슬롯 행 영역(드래그 드롭 등록/해제 히트 판정용). constructor/relayout이 L.slotsRow로 갱신. */
  private slotsRow: Rect;
  /** 드래그 진행 중 플래그 — true면 update의 rebuildCells를 보류(드래그 셀 destroy로 끊김 방지). */
  private dragging = false;
  /** 드래그 중 도착한 최신 데이터 — endDrag에서 1회 적용. */
  private pendingData: ModuleFusionTabData | null = null;
  /** 컨테이너/서브탭 가시성 — false면 update의 rebuild를 스킵(비활성 탭이 refreshAll로 계속 rebuild되는 렉 방지). */
  private visible = true;
  /** 비활성 중 도착한 최신 데이터 — setVisible(true) 재활성 시 1회 적용. */
  private latestData: ModuleFusionTabData | null = null;
  /** 직전 rebuild에 적용한 시그니처(rows + 비용) — 동일하면 update가 rebuild를 스킵(매 flush 불필요 rebuild 렉 방지). */
  private appliedSig: string | null = null;
  /** 레이아웃 재계산용 원본 영역. */
  private readonly rect: { x: number; y: number; width: number; bottomY: number };

  constructor(
    private readonly scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    bottomY: number,
    private readonly opts: ModuleFusionTabViewOptions,
  ) {
    this.rect = { x, y, width, bottomY };
    const L = computeModuleFusionLayout({ x, y, width, height: bottomY - y }, FUSION_MATERIAL_COUNT, this.dir);
    this.slotsRow = L.slotsRow;

    // 콘텐츠 최상단 팁 캐러셀 — 융합 규칙 설명(2~3개 팁)을 랜덤 시작 + ◀/▶ 순환(재사용 컴포넌트, 전체폭). 제목·프롬프트는 제거.
    this.tipView = new TipCarouselView(scene, L.tip.x, L.tip.y, L.tip.width, L.tip.height, "moduleFusion");
    this.tipView.setDepth(2);
    scene.add.existing(this.tipView);

    // 융합 슬롯 2칸 (가로 중앙, RTL이면 순서 미러) + 사이 "+" 기호.
    for (let i = 0; i < FUSION_MATERIAL_COUNT; i++) {
      const cell = L.slotCells[i]!;
      const cx = cell.x + cell.width / 2;
      const cy = cell.y + cell.height / 2;
      const bg = scene.add
        .rectangle(cx, cy, cell.width, cell.height, PALETTE_NUM.cellBg)
        .setStrokeStyle(1, PALETTE_NUM.cellStroke)
        .setDepth(2);
      const label = scene.add
        .text(cx, cy, t("moduleFusion.slotEmpty"), {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.tiny,
          color: PALETTE.textMuted,
          align: "center",
          wordWrap: { width: cell.width - 6 },
        })
        .setOrigin(0.5)
        .setDepth(3);
      this.slots.push({ bg, label });
      this.attachSlotDrag(i);
    }
    for (let i = 0; i < FUSION_MATERIAL_COUNT - 1; i++) {
      const a = L.slotCells[i]!;
      const b = L.slotCells[i + 1]!;
      const px = (a.x + a.width + b.x) / 2;
      const py = a.y + a.height / 2;
      this.plusTexts.push(
        scene.add
          .text(px, py, "+", { fontFamily: FONT_FAMILY, fontSize: TYPO.title, color: PALETTE.textMuted })
          .setOrigin(0.5)
          .setDepth(3),
      );
    }

    this.resultText = scene.add
      .text(L.result.x + L.result.width / 2, L.result.y, t("moduleFusion.resultNone"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.accentAmber,
        align: "center",
      })
      .setOrigin(0.5, 0)
      .setDepth(2);

    // 융합(왼, 비용 표기) + 비우기(오) — 가로 중앙 그룹(방향 무관).
    const btnCy = L.fuseButton.y + L.fuseButton.height / 2;
    const btnRowCx = L.fuseButton.x + L.fuseButton.width / 2;
    const groupW = FUSE_BTN_W + BTN_GAP + CLEAR_BTN_W;
    const fuseCx = btnRowCx - groupW / 2 + FUSE_BTN_W / 2;
    const clearCx = btnRowCx + groupW / 2 - CLEAR_BTN_W / 2;

    this.fuseBtn = new NineSliceButton(scene, fuseCx, btnCy, {
      style: PRIMARY_BUTTON_STYLE,
      label: this.fuseButtonLabel(),
      widthOverride: FUSE_BTN_W,
      heightOverride: L.fuseButton.height,
    });
    this.fuseBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.tryFuse());
    this.fuseBtn.setDepth(3);
    scene.add.existing(this.fuseBtn);

    this.clearBtn = new NineSliceButton(scene, clearCx, btnCy, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("moduleFusion.clearButton"),
      widthOverride: CLEAR_BTN_W,
      heightOverride: L.fuseButton.height,
    });
    this.clearBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.clearSelection());
    this.clearBtn.setDepth(3);
    scene.add.existing(this.clearBtn);

    this.listWidth = L.list.width;
    this.scrollList = new ScrollableList(scene, L.list.x, L.list.y, {
      width: L.list.width,
      height: L.list.height,
      barWidth: 4,
      barColor: PALETTE_NUM.scrollBar,
      trackColor: PALETTE_NUM.scrollTrack,
      trackAlpha: 0.5,
      barAlpha: 0.7,
      scrollSpeed: 36,
      itemMargin: TILE_H, // virtualization — viewport 밖 셀 컬링(모바일 렉 방지)
    });
    this.scrollList.setDepth(2);
    scene.add.existing(this.scrollList);
    this.scrollList.on(SCROLLABLE_LIST_EVENT.Scroll, () => {
      if (!this.visible || this.dragging) return;
      this.rebuildCells();
      this.refreshSelectionVisual();
    });

    // 정렬 컨트롤 — prompt 행 우측(RTL=좌측). 재사용 SortFilterControl(팔레트/합성과 동일).
    this.sortControl = new SortFilterControl<PaletteSortKey>(scene, 0, 0, {
      width: SORT_W,
      keys: PALETTE_SORT_KEYS,
      initial: this.sortState,
      labelOf: (k) => t(`sort.${k}`),
      onChange: (s) => this.onSortChange(s),
      dir: this.dir,
      triggerHeight: SORT_H,
    });
    this.positionSortControl(L);
  }

  /** 정렬 컨트롤 위치 — 전용 sortRow(버튼↔리스트 사이) 우측(RTL=좌측). 어떤 텍스트와도 안 겹침(정적검증). */
  private positionSortControl(L: ReturnType<typeof computeModuleFusionLayout>): void {
    const cx =
      this.dir === "rtl" ? L.sortRow.x + SORT_W / 2 : L.sortRow.x + L.sortRow.width - SORT_W / 2;
    this.sortControl.setPosition(cx, L.sortRow.y + L.sortRow.height / 2);
  }

  /** 정렬 변경 — 현재 행 재정렬 후 즉시 rebuild(데이터 변화 없어 update 시그니처 게이트 우회). */
  private onSortChange(s: SortState<PaletteSortKey>): void {
    this.sortState = s;
    this.rows = applySort(this.rows, s, PALETTE_KEY_EXTRACTORS, paletteTieBreak);
    this.rebuildCells();
    this.refreshSelectionVisual();
  }

  /** 미배치·미융합 모듈 그리드 재구성 + 비용/선택/슬롯/버튼 갱신. 융합으로 사라진 선택 id는 제거. */
  update(data: ModuleFusionTabData): void {
    this.latestData = data; // 비활성·드래그 중에도 최신 데이터는 항상 보관
    // 비활성(탭 나감 또는 숨은 서브탭)이면 rebuild 스킵 — refreshAll이 50ms마다 호출해도 일하지 않는다.
    // (재진입 시 setVisible(true)가 latestData로 1회 적용.) 탭 나간 후 렉 유지 방지.
    if (!this.visible) return;
    // 드래그 중에는 rebuildCells(드래그 셀 흔듦)를 보류 — 드래그가 끊기지 않게. endDrag가 flush.
    if (this.dragging) {
      this.pendingData = data;
      return;
    }
    // 데이터(미배치 모듈 + 융합 비용) 변화 없으면 rebuild 스킵 — 공·벽돌 mutation은 인벤토리/비용을 안 바꾼다.
    const sig =
      moduleRowsSignature(data.modules) +
      `#${data.cost.kind}:${data.cost.kind === "diamond" ? data.cost.amount : 0}`;
    if (sig === this.appliedSig) return;
    this.appliedSig = sig;
    this.applyData(data);
  }

  /** 실제 그리드/선택 갱신 — update(활성 시)와 setVisible(true) 재활성 시 공유. */
  private applyData(data: ModuleFusionTabData): void {
    this.rows = applySort(data.modules, this.sortState, PALETTE_KEY_EXTRACTORS, paletteTieBreak);
    this.cost = data.cost;
    this.selectedIds = this.selectedIds.filter((id) =>
      this.rows.some((r) => r.instanceId === id),
    );
    this.rebuildCells();
    this.refreshSelectionVisual();
  }

  /** i번째 셀의 그리드 로컬 위치(center-origin, RTL이면 col 미러). 풀 셀 위치는 인덱스 기반이라 고정. */
  private cellPos(i: number): { lx: number; ly: number } {
    const perRow = Math.max(1, Math.floor((this.listWidth + TILE_GAP) / (TILE_W + TILE_GAP)));
    const col = i % perRow;
    const r = Math.floor(i / perRow);
    const lxBase = TILE_W / 2 + col * (TILE_W + TILE_GAP);
    const lx = this.dir === "rtl" ? this.listWidth - lxBase : lxBase;
    const ly = TILE_H / 2 + r * (TILE_H + TILE_GAP);
    return { lx, ly };
  }

  private visibleWindow(
    rowsLength: number,
    perRow: number,
    totalRows: number,
    scrollY: number,
  ): { startIndex: number; poolCount: number } {
    if (rowsLength <= 0 || totalRows <= 0) return { startIndex: 0, poolCount: 0 };
    const stride = TILE_H + TILE_GAP;
    const visibleRows = Math.max(1, Math.ceil(this.scrollList.viewHeight / stride));
    const poolRows = Math.min(totalRows, visibleRows + POOL_OVERSCAN_ROWS * 2);
    const maxStartRow = Math.max(0, totalRows - poolRows);
    const startRow = Math.min(maxStartRow, Math.max(0, Math.floor(scrollY / stride) - POOL_OVERSCAN_ROWS));
    const startIndex = startRow * perRow;
    const poolCount = Math.max(0, Math.min(rowsLength - startIndex, poolRows * perRow));
    return { startIndex, poolCount };
  }

  /**
   * 셀 풀 재사용 — destroy/recreate 없이 viewport+overscan 셀에 row를 배치하고 잉여 셀은 viewport 밖으로 컬링한다.
   * 위치는 relayoutContent로 일괄 갱신(드래그 중 frozen 셀은 내부에서 skip). 색/텍스트는 renderCell.
   */
  private rebuildCells(): void {
    const perRow = Math.max(1, Math.floor((this.listWidth + TILE_GAP) / (TILE_W + TILE_GAP)));
    const totalRows = Math.ceil(this.rows.length / perRow);
    const contentH = totalRows > 0 ? totalRows * (TILE_H + TILE_GAP) - TILE_GAP : 0;
    const clampedScrollY = Math.min(this.scrollList.scrollY, Math.max(0, contentH - this.scrollList.viewHeight));
    const { startIndex, poolCount } = this.visibleWindow(this.rows.length, perRow, totalRows, clampedScrollY);
    this.ensureCells(poolCount);
    this.scrollList.setContentHeight(contentH);
    const offY = contentH + this.scrollList.viewHeight + TILE_H * 2;
    const positions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[] = [];
    this.cells.forEach((cell, i) => {
      const rowIndex = startIndex + i;
      if (i < poolCount && rowIndex < this.rows.length) {
        const { lx, ly } = this.cellPos(rowIndex);
        cell.homeX = lx;
        cell.homeY = ly;
        cell.row = this.rows[rowIndex];
        positions.push({ obj: cell.bg, lx, ly }, { obj: cell.label, lx, ly });
        this.renderCell(cell, this.rows[rowIndex]!);
      } else {
        cell.row = undefined;
        positions.push(
          { obj: cell.bg, lx: cell.homeX, ly: offY },
          { obj: cell.label, lx: cell.homeX, ly: offY },
        );
      }
    });
    this.scrollList.relayoutContent(positions);
  }

  /** 활성 셀의 텍스트/색을 현재 row로 갱신(stroke/dim은 refreshSelectionVisual이 선택 반영). */
  private renderCell(cell: CellEntry, row: ModuleFusionRow): void {
    const color = RARITY_COLOR[row.rarity] ?? PALETTE.textPrimary;
    // 풀 재사용 — 잉여(컬링)였던 셀이 재활성될 때 이전 dim(0.35) alpha 잔류 방지(refreshSelectionVisual이
    // 곧 재설정하나, 선택 기준 base가 없을 땐 dim 분기를 안 타므로 여기서 기본값으로 초기화).
    cell.bg.setAlpha(1);
    cell.label.setAlpha(1);
    cell.label.setText(tileLabelText(row)).setColor(color);
  }

  /** 셀 풀을 count 이상으로 확장 — 부족분만 생성(bg/label/드래그 핸들러 1회, i 캡처 동적 조회). */
  private ensureCells(count: number): void {
    while (this.cells.length < count) {
      const i = this.cells.length;
      const bg = this.scene.add
        .rectangle(0, 0, TILE_W, TILE_H, PALETTE_NUM.buttonBg)
        .setStrokeStyle(1, PALETTE_NUM.cellStroke)
        .setDepth(5);
      const label = this.scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.tiny, color: PALETTE.textPrimary, align: "center" })
        .setOrigin(0.5)
        .setDepth(5);
      this.scrollList.addContent(bg, 0, 0);
      this.scrollList.addContent(label, 0, 0);
      const cell: CellEntry = { bg, label, homeX: 0, homeY: 0 };
      this.cells.push(cell);
      // 클릭(16px 미만): 등록된 재료는 바로 해제, 미등록은 정보 팝업. 드래그: 슬롯 행에 드롭하면 등록.
      attachScrollCellDrag(this.scene, this.scrollList, {
        bg,
        label,
        getHome: () => ({ x: this.cells[i]!.homeX, y: this.cells[i]!.homeY }),
        onClick: () => {
          const row = this.cells[i]!.row;
          if (!row) return;
          if (this.materialButtonState(row.instanceId).action === "unregister") {
            this.toggleMaterial(row.instanceId);
          } else {
            this.opts.onCellInfo(row.instanceId);
          }
        },
        onDrop: (px, py) => {
          const row = this.cells[i]!.row;
          if (row) this.handleCellDrop(row.instanceId, px, py);
        },
        onDragStart: () => {
          this.dragging = true;
        },
        onDragEnd: () => this.endDrag(),
      });
    }
  }

  /** 드래그 종료 — 보류 플래그 해제 + 드래그 중 도착한 최신 데이터를 1회 적용(이제 rebuild 안전). */
  private endDrag(): void {
    this.dragging = false;
    if (this.pendingData) {
      const data = this.pendingData;
      this.pendingData = null;
      this.update(data);
    }
  }


  /** 그리드 셀 드래그 드롭 — 슬롯 행 영역 안이면 재료 등록. 밖이면 무동작(셀은 이미 home 스냅백). */
  private handleCellDrop(id: string, px: number, py: number): void {
    if (!isInsideSlotsRow(this.slotsRow, px, py)) return;
    if (this.selectedIds.includes(id)) return; // 이미 등록 — 드롭은 등록 전용(중복 토글 방지)
    this.toggleMaterial(id);
  }

  /**
   * 슬롯 드래그 — 채워진 슬롯을 슬롯 행 밖으로 드롭하면 해제. 빈 슬롯은 무반응.
   * 슬롯 bg는 scene 직접 자식이라 reparent 불필요(setDepth만으로 최상위). 그리드 셀의 freeze/mask 코드 금지.
   */
  private attachSlotDrag(i: number): void {
    const slot = this.slots[i]!;
    const bg = slot.bg;
    bg.setInteractive({ draggable: true, useHandCursor: true });
    let homeX = 0;
    let homeY = 0;
    let offX = 0;
    let offY = 0;
    let active = false;
    bg.on("dragstart", (pointer: Phaser.Input.Pointer) => {
      if (!this.selectedIds[i]) {
        active = false; // 빈 슬롯 — 드래그 무반응
        return;
      }
      active = true;
      this.dragging = true; // 드래그 중 refresh 보류(슬롯 라벨 stale 방지 + 일관)
      homeX = bg.x;
      homeY = bg.y;
      offX = bg.x - pointer.x;
      offY = bg.y - pointer.y;
      bg.setDepth(60);
      slot.label.setDepth(61);
    });
    bg.on("drag", (pointer: Phaser.Input.Pointer) => {
      if (!active) return;
      bg.setPosition(pointer.x + offX, pointer.y + offY);
      slot.label.setPosition(pointer.x + offX, pointer.y + offY);
    });
    bg.on("dragend", (pointer: Phaser.Input.Pointer) => {
      if (!active) return;
      active = false;
      bg.setPosition(homeX, homeY).setDepth(2);
      slot.label.setPosition(homeX, homeY).setDepth(3);
      if (!isInsideSlotsRow(this.slotsRow, pointer.x, pointer.y)) {
        const id = this.selectedIds[i];
        if (id) this.toggleMaterial(id); // 해제 → refreshSelectionVisual이 슬롯 라벨 재배치
      }
      this.endDrag(); // 보류된 refresh flush
    });
  }

  private rowById(id: string): ModuleFusionRow | undefined {
    return this.rows.find((r) => r.instanceId === id);
  }

  /** 선택 기준 모듈 (첫 선택). 없으면 undefined. */
  private baseRow(): ModuleFusionRow | undefined {
    return this.selectedIds.length > 0 ? this.rowById(this.selectedIds[0]!) : undefined;
  }

  /**
   * 융합 재료 등록/해제 토글 — 정보 팝업 하단 등록 버튼이 호출. 이미 선택됨이면 해제, 아니면 추가.
   * 기준(첫 선택)과 footprint가 다른 모듈·슬롯 만석은 추가하지 않는다(버튼이 disabled이나 방어).
   */
  toggleMaterial(id: string): void {
    const idx = this.selectedIds.indexOf(id);
    if (idx >= 0) {
      this.selectedIds.splice(idx, 1);
    } else {
      if (this.selectedIds.length >= FUSION_MATERIAL_COUNT) return;
      const row = this.rowById(id);
      if (!row) return;
      const base = this.baseRow();
      if (base && row.footprintId !== base.footprintId) return; // 같은 footprint만
      this.selectedIds.push(id);
    }
    this.refreshSelectionVisual();
  }

  /** 정보 팝업 등록 버튼 상태 — footprint 기준(register/unregister + enabled). */
  materialButtonState(id: string): MaterialButtonState {
    return resolveFusionMaterialButtonState(this.selectedIds, this.rows, id);
  }

  private fuseButtonLabel(): string {
    switch (this.cost.kind) {
      case "free":
        return t("moduleFusion.fuseButtonFree");
      case "ad":
        return t("moduleFusion.fuseButtonAd");
      case "diamond":
        return t("moduleFusion.fuseButtonDiamond", { amount: this.cost.amount });
    }
  }

  private refreshSelectionVisual(): void {
    const base = this.baseRow();
    for (let i = 0; i < FUSION_MATERIAL_COUNT; i++) {
      const id = this.selectedIds[i];
      const row = id ? this.rowById(id) : undefined;
      if (row) {
        this.slots[i]!.label.setText(tileLabelText(row));
        this.slots[i]!.label.setColor(RARITY_COLOR[row.rarity] ?? PALETTE.textPrimary);
        this.slots[i]!.bg.setStrokeStyle(2, hexToNum(RARITY_COLOR[row.rarity] ?? PALETTE.textPrimary));
      } else {
        this.slots[i]!.label.setText(t("moduleFusion.slotEmpty"));
        this.slots[i]!.label.setColor(PALETTE.textMuted);
        this.slots[i]!.bg.setStrokeStyle(1, PALETTE_NUM.cellStroke);
      }
    }
    // 결과 미리보기 — 2개 선택 시 "A + B" 합쳐짐.
    const ready = this.selectedIds.length === FUSION_MATERIAL_COUNT;
    const a = this.baseRow();
    const b = this.selectedIds[1] ? this.rowById(this.selectedIds[1]!) : undefined;
    if (ready && a && b) {
      this.resultText.setText(t("moduleFusion.resultPreview", { a: a.name, b: b.name }));
    } else {
      this.resultText.setText(t("moduleFusion.resultNone"));
    }
    this.fuseBtn.setLabel(this.fuseButtonLabel());
    setButtonEnabled(this.fuseBtn, ready);
    setButtonEnabled(this.clearBtn, this.selectedIds.length > 0);
    // 하단 셀 dim — 기준과 footprint 다른 모듈은 반투명+비강조. 활성 범위(rows.length)만(잉여 셀은 컬링).
    for (const cell of this.cells) {
      if (!cell?.row) continue;
      const selected = this.selectedIds.includes(cell.row.instanceId);
      const matches = !base || cell.row.footprintId === base.footprintId;
      const dim = base != null && !matches;
      cell.bg.setAlpha(dim ? 0.35 : 1);
      cell.label.setAlpha(dim ? 0.35 : 1);
      cell.bg.setStrokeStyle(
        selected ? 2 : 1,
        selected ? PALETTE_NUM.selectionStroke : hexToNum(RARITY_COLOR[cell.row.rarity] ?? PALETTE.textPrimary),
      );
    }
  }

  private tryFuse(): void {
    if (this.selectedIds.length !== FUSION_MATERIAL_COUNT) return;
    this.opts.onFuse([...this.selectedIds]);
    // 선택 초기화 — GameScene이 곧 refresh → update(새 data)로 그리드/슬롯을 갱신한다.
    this.selectedIds = [];
  }

  /** 비우기 버튼 — 등록된 재료 전부 해제. */
  private clearSelection(): void {
    if (this.selectedIds.length === 0) return;
    this.selectedIds = [];
    this.refreshSelectionVisual();
  }

  /** 언어 변경 — 정적 라벨만 갱신. */
  applyLanguage(): void {
    this.tipView.applyLanguage();
    this.clearBtn.setLabel(t("moduleFusion.clearButton"));
    this.sortControl.applyLanguage();
    this.refreshSelectionVisual();
  }

  /** 방향(LTR↔RTL) 전환 — 텍스트 정렬/Bidi + 슬롯 순서 + 미배치 그리드 col 미러 재배치. */
  relayout(dir: TextDirection): void {
    this.dir = dir;
    const L = computeModuleFusionLayout(
      { x: this.rect.x, y: this.rect.y, width: this.rect.width, height: this.rect.bottomY - this.rect.y },
      FUSION_MATERIAL_COUNT,
      dir,
    );
    this.slotsRow = L.slotsRow;
    this.tipView.setPosition(L.tip.x, L.tip.y);
    this.tipView.relayout(dir);
    for (let i = 0; i < FUSION_MATERIAL_COUNT; i++) {
      const cell = L.slotCells[i]!;
      const cx = cell.x + cell.width / 2;
      const cy = cell.y + cell.height / 2;
      this.slots[i]!.bg.setPosition(cx, cy);
      this.slots[i]!.label.setPosition(cx, cy);
    }
    for (let i = 0; i < FUSION_MATERIAL_COUNT - 1; i++) {
      const a = L.slotCells[i]!;
      const b = L.slotCells[i + 1]!;
      this.plusTexts[i]!.setPosition((a.x + a.width + b.x) / 2, a.y + a.height / 2);
    }
    this.sortControl.setDirection(dir);
    this.positionSortControl(L);
    this.rebuildCells();
    this.refreshSelectionVisual();
  }

  /** 셸 펼치기/접기 — 새 콘텐츠 rect로 결과/버튼/스크롤(세로 영향)까지 완전 재배치 후 relayout(슬롯/정렬/셀). */
  resize(rect: { x: number; y: number; width: number; height: number }): void {
    this.rect.x = rect.x;
    this.rect.y = rect.y;
    this.rect.width = rect.width;
    this.rect.bottomY = rect.y + rect.height;
    const L = computeModuleFusionLayout(
      { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
      FUSION_MATERIAL_COUNT,
      this.dir,
    );
    this.resultText.setPosition(L.result.x + L.result.width / 2, L.result.y);
    const btnCy = L.fuseButton.y + L.fuseButton.height / 2;
    const btnRowCx = L.fuseButton.x + L.fuseButton.width / 2;
    const groupW = FUSE_BTN_W + BTN_GAP + CLEAR_BTN_W;
    this.fuseBtn.setPosition(btnRowCx - groupW / 2 + FUSE_BTN_W / 2, btnCy);
    this.clearBtn.setPosition(btnRowCx + groupW / 2 - CLEAR_BTN_W / 2, btnCy);
    this.listWidth = L.list.width;
    this.scrollList.resize(L.list.x, L.list.y, L.list.width, L.list.height);
    this.relayout(this.dir);
  }

  setVisible(visible: boolean): void {
    const wasHidden = !this.visible;
    this.visible = visible;
    this.sortControl.setVisible(visible);
    this.tipView.setVisible(visible);
    for (const s of this.slots) {
      setVisibleActive(s.bg, visible);
      setVisibleActive(s.label, visible);
    }
    for (const p of this.plusTexts) setVisibleActive(p, visible);
    setVisibleActive(this.resultText, visible);
    this.fuseBtn.setVisible(visible);
    this.clearBtn.setVisible(visible);
    this.scrollList.setVisible(visible);
    // 비활성 동안 스킵된 update를 재진입 시 1회 반영(탭 나간 사이 변동된 인벤토리).
    // appliedSig 무효화 후 update 경유 — 재진입 시 데이터 동일해도 1회는 강제 rebuild(화면 복원).
    if (visible && wasHidden && this.latestData) {
      this.appliedSig = null;
      this.update(this.latestData);
    }
  }
}
